<template>
  <div class="bg-white border rounded shadow p-4 space-y-4">
    <div class="space-y-1">
      <div class="flex items-center justify-between gap-2">
        <router-link
          :to="{ name: 'EmployeeDashboard', params: { staffId: staff.id } }"
          class="block text-lg font-semibold text-black hover:text-cerulean transition-colors"
        >
          {{ staff.firstname }} {{ staff.surname }}
        </router-link>
        <div class="flex-shrink-0 rounded-lg border border-gray-200 bg-gray-100 px-3 py-1 text-lg font-semibold tabular-nums text-gray-800">
          <span>Total </span>
          <span>{{ formatHoursToParts(rangeTotals.workedTotal).hours }}</span><span class="text-xs align-super">h</span>
          <span class="ml-1">{{ formatHoursToParts(rangeTotals.workedTotal).minutes }}</span><span class="text-xs align-super">m</span>
        </div>
      </div>
    </div>

    <div class="overflow-x-auto overflow-y-visible pb-10 pt-4 mx-auto w-[50vw] min-w-[360px] max-w-[70vw]">
      <div class="flex items-end justify-center min-w-max bar-week-gap mx-auto">
        <div
          v-for="(week, weekIdx) in weeks"
          :key="`bar-week-${staff.id}-${weekIdx}`"
          class="flex flex-col gap-2 pr-16 flex-none min-w-max"
        >
          <div class="flex items-end gap-4">
            <div
              v-for="date in week.actualDates"
              :key="`${staff.id}-${date}-bar`"
              class="flex flex-col items-center gap-1 flex-none w-20"
            >
              <div
                class="relative"
                @mouseenter="showTooltip($event, date)"
                @mouseleave="$emit('hide-tooltip')"
              >
                <div class="flex h-64 w-20 flex-col justify-end overflow-hidden rounded bg-gray-100">
                  <div
                    class="w-full"
                    style="background-color: #EF4444;"
                    :style="{ height: barHeight(hoursFor(date).unaccounted) }"
                  />
                  <div
                    class="w-full"
                    style="background-color: #FFD362;"
                    :style="{ height: barHeight(hoursFor(date).internalNonBillable) }"
                  />
                  <div
                    class="w-full"
                    style="background-color: #C4B5FD;"
                    :style="{ height: barHeight(hoursFor(date).pto) }"
                  />
                  <div
                    v-if="!includeClientAsBillable"
                    class="w-full"
                    style="background-color: #7FD2E2;"
                    :style="{ height: barHeight(hoursFor(date).clientNonBillable) }"
                  />
                  <div
                    class="w-full"
                    style="background-color: #98D87B;"
                    :style="{ height: barHeight(barBillableHours(date)) }"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            class="my-3 relative z-10 w-full"
            style="height: 4px; background: #cbd5e1;"
          />
          <div class="flex items-start gap-4">
            <div
              v-for="date in week.actualDates"
              :key="`${staff.id}-${date}-label`"
              class="flex flex-col items-center gap-1 flex-none w-20"
            >
              <div class="text-[11px] font-semibold text-gray-700 text-center leading-tight">
                <div>{{ formatBarDay(date) }}</div>
                <div>{{ formatBarMonthDay(date) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!isCollapsed"
      class="pt-2"
    >
      <BillableGauge
        :staff-id="staff.id"
        :include-client-non-billable="includeClientAsBillable"
        layout="horizontal"
      />
    </div>
    <div
      v-if="hasClientNonBillableHours"
      class="flex justify-end"
    >
      <label class="flex items-center gap-2 text-xs text-gray-600 font-semibold">
        <span>Combine billable and client non-billable</span>
        <input
          type="checkbox"
          class="sr-only"
          :checked="includeClientAsBillable"
          @change="$emit('update-include-client', staff.id, $event.target.checked)"
        >
        <span
          class="relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full border border-gray-300 transition-colors"
          :class="includeClientAsBillable ? 'bg-cerulean' : 'bg-gray-200'"
        >
          <span
            class="inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform"
            :class="includeClientAsBillable ? 'translate-x-4' : 'translate-x-0.5'"
          />
        </span>
      </label>
    </div>
    <button
      type="button"
      class="mt-2 flex items-center justify-center text-gray-500 hover:text-cerulean"
      :aria-label="isCollapsed ? 'Expand gauge' : 'Collapse gauge'"
      @click="$emit('toggle-gauge', staff.id)"
    >
      <ChevronDownIcon
        v-if="isCollapsed"
        class="h-5 w-5"
      />
      <ChevronUpIcon
        v-else
        class="h-5 w-5"
      />
    </button>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUpdate, onMounted, onUpdated } from 'vue'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-vue-next'
import BillableGauge from '@/EmployeeInsightsComponents/BillableGauge.vue'
import {
  formatBarDay,
  formatBarMonthDay,
  formatHoursToParts,
  formatTooltipDate,
  staffRangeTotals,
  statForDate,
  statHours
} from '@/utils/employeeInsightsCalculations'

const props = defineProps({
  staff: { type: Object, required: true },
  weeks: { type: Array, required: true },
  dailyStatsByStaff: { type: Object, required: true },
  dailyStatsByStaffDateIndex: { type: Object, default: () => ({}) },
  maxTotalHrs: { type: Number, required: true },
  isCollapsed: { type: Boolean, default: false },
  includeClientAsBillable: { type: Boolean, default: false }
})

const emit = defineEmits(['toggle-gauge', 'update-include-client', 'show-tooltip', 'hide-tooltip'])

const setupStartedAt = performance.now()
let updateStartedAt = null

const rangeTotals = computed(() => staffRangeTotals(props.dailyStatsByStaff, props.staff.id))
const hasClientNonBillableHours = computed(() => rangeTotals.value.clientNonBillable > 0)
const renderedBarCount = computed(() =>
  props.weeks.reduce((total, week) => total + week.actualDates.length, 0)
)

function barRenderDetails() {
  return {
    staffId: props.staff.id,
    staffName: `${props.staff.firstname || ''} ${props.staff.surname || ''}`.trim(),
    weekCount: props.weeks.length,
    renderedBarCount: renderedBarCount.value,
    renderedSegmentCount: renderedBarCount.value * 4,
    isCollapsed: props.isCollapsed,
    hasBillableGauge: !props.isCollapsed,
    statCount: props.dailyStatsByStaff?.[props.staff.id]?.length || 0,
    maxTotalHrs: props.maxTotalHrs
  }
}

function logAfterPaint(label, startedAt) {
  nextTick(() => {
    requestAnimationFrame(() => {
      const durationMs = Math.round(performance.now() - startedAt)
      console.info(label, {
        durationMs,
        paintEstimateMs: durationMs,
        ...barRenderDetails()
      })
    })
  })
}

onMounted(() => {
  logAfterPaint('[Staff Insights] bar graph mounted', setupStartedAt)
})

onBeforeUpdate(() => {
  updateStartedAt = performance.now()
})

onUpdated(() => {
  if (!updateStartedAt) return
  const updateMs = Math.round(performance.now() - updateStartedAt)
  const details = barRenderDetails()
  if (updateMs >= 8 || details.renderedBarCount >= 90) {
    console.info('[Staff Insights] bar graph updated', {
      durationMs: updateMs,
      updateMs,
      ...details
    })
  }
  updateStartedAt = null
})

function statFor(date) {
  return statForDate(props.dailyStatsByStaff, props.staff.id, date, props.dailyStatsByStaffDateIndex)
}

function hoursFor(date) {
  return statHours(statFor(date))
}

function barBillableHours(date) {
  const hours = hoursFor(date)
  return props.includeClientAsBillable
    ? hours.billable + hours.clientNonBillable
    : hours.billable
}

function barHeight(hours) {
  const denom = props.maxTotalHrs || 1
  return `${(hours / denom) * 100}%`
}

function showTooltip(event, date) {
  const hours = hoursFor(date)
  const billableLines = props.includeClientAsBillable
    ? [`${(hours.billable + hours.clientNonBillable).toFixed(2)}h billable + client non-billable`]
    : [
        `${hours.billable.toFixed(2)}h billable`,
        `${hours.clientNonBillable.toFixed(2)}h client non-billable`
      ]
  const body = [
    ...billableLines,
    `${hours.internalNonBillable.toFixed(2)}h internal non-billable`,
    `${hours.pto.toFixed(2)}h PTO`,
    `${hours.unaccounted.toFixed(2)}h unaccounted`
  ].join(' • ')

  emit('show-tooltip', {
    event,
    title: formatTooltipDate(date),
    body
  })
}
</script>

<style scoped>
.bar-week-gap {
  gap: 3.5rem;
}
</style>
